import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ChangeDetectionStrategy,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatSlider, MatSliderThumb } from '@angular/material/slider';

@Component({
  selector: 'sg-slider',
  templateUrl: './sg-slider.component.html',
  styleUrls: ['./sg-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SgSliderComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class SgSliderComponent implements ControlValueAccessor, AfterViewInit {
  @ViewChild('matSlider', { static: false }) matSlider: MatSlider;
  @ViewChild(MatSliderThumb, { static: false }) sliderThumb: MatSliderThumb;

  @Input() min = 0;
  @Input() max = 100;
  @Input() step = 1;
  @Input() showTooltip = true;

  private _disabled = false;
  @Input()
  set disabled(value: boolean) {
    this._disabled = value;
    this.cdr.markForCheck();
  }
  get disabled(): boolean {
    return this._disabled;
  }

  @Input() showPercentage = false;

  private _value = 0;
  private _isInitialized = false;

  @Input()
  set value(val: number) {
    this._value = val ?? 0;

    if (this.sliderThumb && this._isInitialized) {
      this.sliderThumb.writeValue(this._value);
    }

    if (this.cdr) {
      this.cdr.detectChanges();
    }
  }
  get value(): number {
    return this._value;
  }

  @Output() valueChange = new EventEmitter<number>();
  @Output() slideEnd = new EventEmitter<number>();

  private onChange?: (value: number) => void;
  private onTouched?: () => void;

  formatLabel = (value: number): string => {
    if (this.step < 1 && value % 1 !== 0) {
      const decimals = this.step.toString().split('.')[1]?.length || 0;
      const formatted = value.toFixed(decimals);
      return this.showPercentage ? `${formatted}%` : formatted;
    }

    return this.showPercentage ? `${value}%` : value.toString();
  };

  constructor(private cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    const initialValue = this._value;

    setTimeout(() => {
      this._value = initialValue;
      this._isInitialized = true;
      this.cdr.detectChanges();
    }, 0);
  }

  writeValue(value: number): void {
    if (value != null) {
      this._value = value;

      if (this.sliderThumb) {
        this.sliderThumb.writeValue(value);
      }

      this.cdr.detectChanges();
    }
  }

  registerOnChange(fn: (value: number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
    this.cdr.markForCheck();
  }

  onSliderChange(value: number): void {
    this._value = value || 0;

    if (this.onChange) {
      this.onChange(this._value);
    }
    this.valueChange.emit(this._value);
    this.slideEnd.emit(this._value);
    this.cdr.markForCheck();
  }

  onSliderInput(event: Event): void {
    if (!this._isInitialized) {
      return;
    }

    const target = event.target as HTMLInputElement;
    this._value = Number(target.value) || 0;

    if (this.onTouched) {
      this.onTouched();
    }

    this.cdr.markForCheck();
  }
}
